import { useState } from 'react';
import { Loader2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const REASONS = [
  { v: 'taking_a_break', label: 'Taking a break (might be back)' },
  { v: 'found_other_platform', label: 'Found a different platform' },
  { v: 'not_enough_business', label: "Didn't get enough business" },
  { v: 'technical_issues', label: 'Technical issues or bugs' },
  { v: 'personal_reasons', label: 'Personal reasons' },
  { v: 'other', label: 'Other' },
];

const RETURNING = [
  { v: 'yes_soon', label: 'Yes, soon (within 30 days)' },
  { v: 'maybe_someday', label: 'Maybe someday' },
  { v: 'no_permanently', label: "No, leaving permanently" },
];

/** Drop-in modal for the existing "Delete Account" buttons. Renders a 3-question
 *  exit survey then calls DELETE /api/users/delete-account with confirm:true. */
export default function DeleteAccountModal({ open, onClose, onDeleted }) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [returning, setReturning] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/users/delete-account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reason, returning, feedback, confirm: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Could not delete account', { duration: 8000 });
        setLoading(false);
        return;
      }
      toast.success(data.message || 'Account deleted');
      try { localStorage.removeItem('auth_token'); } catch {}
      onDeleted?.();
      window.location.href = '/';
    } catch {
      toast.error('Network error');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-lg w-full rounded-2xl bg-[#13153A] border border-red-500/40 shadow-[0_30px_80px_-15px_rgba(239,68,68,0.45)] p-6 sm:p-7 relative" data-testid="delete-account-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white" data-testid="close-delete-modal"><X size={18} /></button>

        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500/15 text-red-400 mb-4">
          <AlertTriangle size={22} />
        </div>
        <h2 className="font-syne text-2xl font-black text-white mb-2">Before you go…</h2>
        <p className="text-zinc-300 text-sm mb-5">3 quick questions help us improve. Active vendor jobs must be completed or cancelled first.</p>

        {step === 1 && (
          <div data-testid="delete-step-1">
            <p className="font-semibold text-white mb-2">1. Why are you closing your account?</p>
            <div className="space-y-2 mb-5">
              {REASONS.map((r) => (
                <label key={r.v} className="flex items-center gap-2 cursor-pointer text-zinc-200 text-sm p-2 rounded-lg hover:bg-white/5">
                  <input type="radio" name="reason" value={r.v} checked={reason === r.v} onChange={(e) => setReason(e.target.value)} data-testid={`reason-${r.v}`} />
                  {r.label}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 text-sm font-semibold">Cancel</button>
              <button onClick={() => setStep(2)} disabled={!reason} className="flex-1 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm disabled:opacity-50" data-testid="delete-next-1">Next</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="delete-step-2">
            <p className="font-semibold text-white mb-2">2. Do you plan to return?</p>
            <div className="space-y-2 mb-5">
              {RETURNING.map((r) => (
                <label key={r.v} className="flex items-center gap-2 cursor-pointer text-zinc-200 text-sm p-2 rounded-lg hover:bg-white/5">
                  <input type="radio" name="returning" value={r.v} checked={returning === r.v} onChange={(e) => setReturning(e.target.value)} data-testid={`returning-${r.v}`} />
                  {r.label}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 text-sm font-semibold">Back</button>
              <button onClick={() => setStep(3)} disabled={!returning} className="flex-1 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm disabled:opacity-50" data-testid="delete-next-2">Next</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="delete-step-3">
            <p className="font-semibold text-white mb-2">3. Anything we could have done differently?</p>
            <textarea
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Optional — your honest feedback helps us improve."
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-red-400/60 mb-5"
              data-testid="delete-feedback"
            />
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 mb-5 text-red-200 text-xs">
              <strong>This is permanent.</strong> Your profile, services, and content will be removed from public view. This cannot be undone except by a master admin.
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(2)} disabled={loading} className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-200 text-sm font-semibold disabled:opacity-60">Back</button>
              <button onClick={handleDelete} disabled={loading} className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-delete-account">
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? 'Deleting…' : 'Delete my account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
